import { z } from "zod";

import { enumReadSchema } from "../../core/enum-read.schema";

/**
 * Which of an app's two data jobs an operator is asking to re-run now.
 *
 * The pair is deliberately NOT one job with a flag. They fail independently,
 * clear independently, and answer different questions — a dump that stopped
 * arriving and a dump that arrives but cannot be restored are different
 * incidents with different next steps — which is why the issue registry carries
 * four separate codes across them rather than one.
 *
 * `restore_drill` also carries a second effect the other does not: forcing a
 * drill overrides the weekly per-app rotation for the day, because a drill is
 * otherwise projected only on the app's own staggered rotation day.
 */
export const VIBE_APP_DATA_RUN_KINDS = ["backup", "restore_drill"] as const;

export const VibeAppDataRunKindSchema = z.enum(VIBE_APP_DATA_RUN_KINDS);

export type VibeAppDataRunKind = z.infer<typeof VibeAppDataRunKindSchema>;

/**
 * What asking for a run actually did — the honest set, refusals included.
 *
 * 🔴 EVERY REFUSAL NAMES A DIFFERENT UPSTREAM FIX, WHICH IS WHY THERE ARE FOUR
 * OF THEM RATHER THAN ONE `false`. This lever records an intent the tenant's own
 * agent honours on its next poll; the control plane cannot reach the Nomad that
 * runs the job, so it can never report that a run succeeded. What it CAN do is
 * refuse in advance whenever the projection would emit no directive at all —
 * and that is the entire value, because the alternative is a button that
 * reports success and is then silently a no-op forever.
 *
 * The four are exactly the gates the projection itself applies:
 *
 *   - `no_managed_data`            — the app has no live data binding, so there
 *                                    is no database to dump. Bind managed data.
 *   - `no_dump_to_drill`           — no dump has provably landed for this app,
 *                                    so there is nothing to restore. Run the
 *                                    backup first.
 *   - `backup_destination_not_ready` /
 *     `drill_destination_not_ready` — the tenant's `app-backup` stack has not
 *                                    applied (no bucket, no CMK, or no drill
 *                                    role). Converge the cluster first.
 */
export const VIBE_APP_DATA_RUN_OUTCOMES = [
  "requested",
  "no_managed_data",
  "no_dump_to_drill",
  "backup_destination_not_ready",
  "drill_destination_not_ready"
] as const;

export const VibeAppDataRunOutcomeSchema = z.enum(VIBE_APP_DATA_RUN_OUTCOMES);

/** `VibeAppDataRunOutcomeSchema` as a reader parses it: a value this build does not list reads as unlisted. */
export const VibeAppDataRunOutcomeReadSchema = enumReadSchema(VIBE_APP_DATA_RUN_OUTCOMES);

export type VibeAppDataRunOutcome = z.infer<typeof VibeAppDataRunOutcomeSchema>;
